import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import InvestorPageShell from "../../../components/Investor/InvestorPageShell";
import InvestorRequestsNav, { getRequestFilter } from "../../../components/Investor/InvestorRequestsNav";
import InvestorListPagination from "../../../components/Investor/InvestorListPagination";
import InvestorRequestsList from "../../../components/Investor/InvestorRequestsList";
import { useListInvestorInvestmentRequestsQuery } from "../../../store/investor/investorInvestmentsApi";
import { useListMarketplaceOrdersQuery } from "../../../store/investor/investorMarketplaceApi";
import { useListRealEstateRequestsQuery } from "../../../store/investor/investorRealEstateApi";
import { mapInvestorRequestToListShape } from "../../../utils/investorInvestmentsAdapter";
import TableSkeletonLoader from "../../../components/Common/TableSkeletonLoader";
import { InvestorApiErrorState } from "../../../components/Investor/InvestorPlatformState";

const REQUESTS_PER_PAGE = 3;

/**
 * Merge strategy for "All":
 * Each source is fetched (up to 50 latest). Results are merged and sorted by latest activity client-side.
 * Per-type tabs use API pagination. Global All pagination is approximate across sources.
 */
export default function InvestorRequestsPage() {
  const [searchParams] = useSearchParams();
  const filter = getRequestFilter(searchParams);
  const [page, setPage] = useState(1);

  const isMarketplace = filter === "marketplace";
  const isInvestmentOnly = filter === "investment";
  const isRealEstate = filter === "real_estate";
  const isAll = filter === "all";
  const loadInvestments = isAll || isInvestmentOnly;
  const loadMarketplace = isAll || isMarketplace;
  const loadRealEstate = isAll || isRealEstate;

  const {
    data: apiData,
    isFetching: isFetchingInvestments,
    isError: isInvestmentError,
    refetch: refetchInvestments,
  } = useListInvestorInvestmentRequestsQuery(
    { page: isAll ? 1 : page, per_page: isAll ? 50 : REQUESTS_PER_PAGE },
    { skip: !loadInvestments }
  );

  const {
    data: marketplaceData,
    isFetching: isFetchingMarketplace,
    isError: isMarketplaceError,
    refetch: refetchMarketplace,
  } = useListMarketplaceOrdersQuery(
    { page: isAll ? 1 : page, per_page: isAll ? 50 : REQUESTS_PER_PAGE },
    { skip: !loadMarketplace }
  );

  const {
    data: realEstateData,
    isFetching: isFetchingRealEstate,
    isError: isRealEstateError,
    refetch: refetchRealEstate,
  } = useListRealEstateRequestsQuery(
    { page: isAll ? 1 : page, per_page: isAll ? 50 : REQUESTS_PER_PAGE },
    { skip: !loadRealEstate }
  );

  useEffect(() => {
    setPage(1);
  }, [filter]);

  const investmentRequests = useMemo(
    () => (apiData?.items || []).map(mapInvestorRequestToListShape).filter(Boolean),
    [apiData?.items]
  );

  const marketplaceOrders = useMemo(() => marketplaceData?.items || [], [marketplaceData?.items]);
  const realEstateRequests = useMemo(() => realEstateData?.items || [], [realEstateData?.items]);

  const mergedAll = useMemo(() => {
    if (!isAll) return [];
    const combined = [...investmentRequests, ...marketplaceOrders, ...realEstateRequests];
    combined.sort((a, b) => {
      const aTime = new Date(a.submittedAt || a.createdAt || a.created_at || 0).getTime();
      const bTime = new Date(b.submittedAt || b.createdAt || b.created_at || 0).getTime();
      return bTime - aTime;
    });
    return combined;
  }, [isAll, investmentRequests, marketplaceOrders, realEstateRequests]);

  const pageRequests = useMemo(() => {
    if (isMarketplace) return marketplaceOrders;
    if (isInvestmentOnly) return investmentRequests;
    if (isRealEstate) return realEstateRequests;
    if (isAll) {
      const start = (page - 1) * REQUESTS_PER_PAGE;
      return mergedAll.slice(start, start + REQUESTS_PER_PAGE);
    }
    return [];
  }, [
    isMarketplace,
    isInvestmentOnly,
    isRealEstate,
    isAll,
    marketplaceOrders,
    investmentRequests,
    realEstateRequests,
    mergedAll,
    page,
  ]);

  const isFetching =
    (loadInvestments && isFetchingInvestments) ||
    (loadMarketplace && isFetchingMarketplace) ||
    (loadRealEstate && isFetchingRealEstate);
  const isError =
    (loadInvestments && isInvestmentError) ||
    (loadMarketplace && isMarketplaceError) ||
    (loadRealEstate && isRealEstateError);
  const refetch = () => {
    if (loadInvestments) refetchInvestments();
    if (loadMarketplace) refetchMarketplace();
    if (loadRealEstate) refetchRealEstate();
  };

  const total = isMarketplace
    ? marketplaceData?.pagination?.total || marketplaceOrders.length
    : isInvestmentOnly
      ? apiData?.pagination?.total || investmentRequests.length
      : isRealEstate
        ? realEstateData?.pagination?.total || realEstateRequests.length
        : isAll
          ? mergedAll.length
          : 0;
  const totalPages = Math.max(1, Math.ceil(total / REQUESTS_PER_PAGE) || 1);
  const safePage = Math.min(page, totalPages);

  const emptyMessage = isMarketplace
    ? "No marketplace orders yet."
    : isRealEstate
      ? "No property requests yet."
      : isInvestmentOnly
        ? "No investment requests yet."
        : "No requests yet.";

  return (
    <InvestorPageShell
      title="My Requests"
      subtitle="Track investments, marketplace orders, and property requests."
    >
      <InvestorRequestsNav />

      {isFetching ? (
        <TableSkeletonLoader rows={3} columns={2} />
      ) : isError ? (
        <InvestorApiErrorState
          message="We couldn’t load your requests. Please try again."
          onRetry={() => refetch()}
        />
      ) : (
        <InvestorRequestsList requests={pageRequests} emptyMessage={emptyMessage} />
      )}

      <InvestorListPagination
        currentPage={safePage}
        perPage={REQUESTS_PER_PAGE}
        total={total}
        totalPages={totalPages}
        count={pageRequests.length}
        onPageChange={setPage}
      />
    </InvestorPageShell>
  );
}
